/* Nomsa surrounded by thought bubbles.
 * Photorealistic Nomsa (forward-facing with phone) as background — pre-baked bubbles
 * erased in `/prologue/nomsa_thoughts_bg.png`, bottom caption cropped out.
 * Six animated thought bubbles pop in one-by-one at the exact positions of the reference,
 * with subtle bob. Purely visual — no captions at the end.
 */
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

const BG = "/prologue/nomsa_thoughts_bg.png";

/**
 * Positions calibrated to the cropped background (1850x660).
 * Rendered inside a 1600x900 canvas with the image contained at full width (1600x570)
 * centered vertically → y-offset ~165. Each entry is in % of the 1600x900 canvas.
 */
const BUBBLES = [
  { text: "Is this real?",              x: 23.2, y: 46.4, delay: 0.4, tail: "right", w: 240 },
  { text: "Who is this adviser?",       x: 79.9, y: 46.1, delay: 0.9, tail: "left",  w: 300 },
  { text: "Can I trust this?",          x: 22.8, y: 60.7, delay: 1.4, tail: "right", w: 270 },
  { text: "Is this legitimate?",        x: 81.9, y: 61.8, delay: 1.9, tail: "left",  w: 285 },
  { text: "Is this a deepfake?",        x: 25.4, y: 75.7, delay: 2.4, tail: "right", w: 280 },
  { text: "What if I'm being scammed?", x: 78.6, y: 75.7, delay: 2.9, tail: "left",  w: 300, twoLine: true },
];

export default function NomsaThoughtsDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  const scene = (
    <div
      className="relative w-full h-full overflow-hidden"
      data-testid="nomsa-thoughts"
      style={{ background: "#050914" }}
    >
      {/* Blurred, zoomed copy fills the 16:9 canvas so there's no hard letterbox */}
      <img
        src={BG}
        alt=""
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ objectFit: "cover", filter: "blur(28px) brightness(0.55)", transform: "scale(1.15)" }}
      />
      {/* Sharp Nomsa image, contained at full width, centered vertically */}
      <img
        src={BG}
        alt=""
        className="absolute left-0 right-0 w-full"
        style={{
          top: "50%",
          transform: "translateY(-50%)",
          aspectRatio: "1850 / 660",
          objectFit: "cover",
        }}
      />
      {/* Soft vignette to keep focus on Nomsa */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(5,9,20,0.35) 72%, rgba(5,9,20,0.85) 100%)" }} />

      {/* Bubbles */}
      {BUBBLES.map((b, i) => (
        <Bubble key={i} bubble={b} idx={i} />
      ))}
    </div>
  );

  if (embed) {
    return (
      <div className="w-screen h-screen" style={{ background: "#050914" }}>
        {scene}
      </div>
    );
  }
  return (
    <div className="w-full" style={{ background: "#050914", minHeight: "100vh" }}>
      <div className="mx-auto" style={{ maxWidth: 1600, aspectRatio: "16/9" }}>{scene}</div>
    </div>
  );
}

function Bubble({ bubble, idx }) {
  const isLeftSide = bubble.tail === "right"; // bubble on the left column, tail points right toward Nomsa
  return (
    <motion.div
      className="absolute"
      style={{
        left: `${bubble.x}%`,
        top: `${bubble.y}%`,
        width: bubble.w,
        transform: "translate(-50%, -50%)",
      }}
      initial={{ opacity: 0, scale: 0.5, y: 12 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: [0, -6, 0, 6, 0],
      }}
      transition={{
        opacity: { duration: 0.5, delay: bubble.delay },
        scale:   { duration: 0.6, delay: bubble.delay, type: "spring", stiffness: 260, damping: 18 },
        y: { duration: 5 + (idx % 3), delay: bubble.delay + 0.6, repeat: Infinity, ease: "easeInOut" },
      }}
    >
      <BubbleBody text={bubble.text} tailRight={isLeftSide} twoLine={bubble.twoLine} />
    </motion.div>
  );
}

function BubbleBody({ text, tailRight, twoLine }) {
  // Speech balloon: white rounded rect + a triangular pointer on the side facing Nomsa.
  const bg = "rgba(255,255,255,0.96)";
  return (
    <div className="relative">
      <div
        className="rounded-[22px] px-5 py-3 shadow-2xl"
        style={{
          background: bg,
          boxShadow: "0 8px 30px rgba(0,0,0,0.55), 0 0 24px rgba(120,180,255,0.15), inset 0 1px 0 rgba(255,255,255,0.4)",
          border: "1px solid rgba(180,210,255,0.35)",
        }}
      >
        <div
          className="text-slate-900 font-semibold"
          style={{
            fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
            fontSize: 20,
            lineHeight: 1.25,
            whiteSpace: twoLine ? "pre-line" : "nowrap",
            textAlign: "left",
          }}
        >
          {text}
        </div>
      </div>
      {/* Pointer tail toward Nomsa */}
      <div
        className="absolute"
        style={{
          bottom: -14,
          [tailRight ? "right" : "left"]: 22,
          width: 0,
          height: 0,
          borderLeft: "12px solid transparent",
          borderRight: "12px solid transparent",
          borderTop: `18px solid ${bg}`,
          filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.35))",
        }}
      />
    </div>
  );
}
